package it.neckar.lizergy.model.configuration.quote.builder

import com.meistercharts.charts.pv.stringsPlanning.ModuleReference
import com.meistercharts.charts.pv.stringsPlanning.RoofStringsConfiguration
import it.neckar.lizergy.model.configuration.moduleLayout.ModuleLayout.ModuleLayoutId
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayouts
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ModulesString
import it.neckar.lizergy.model.configuration.quote.builder.InverterType.InverterId
import it.neckar.open.collections.fastForEach
import it.neckar.uuid.HasUuid
import it.neckar.uuid.HasValueClassUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.ValueClassUuid
import it.neckar.uuid.plus
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlin.uuid.Uuid

interface InverterConfiguration : HasValueClassUuid {
  val inverterId: InverterId

  val inverterIndex: Int

  val mpptInputConfigurations: List<MpptInputConfiguration>


  override val id: InverterConfigurationId
    get() = InverterConfigurationId(inverterId.uuid + inverterIndex)

  val numberOfOptimalModules: Int
    get() = mpptInputConfigurations.sumOf { it.numberOfOptimalModules }

  val numberOfPlannedModules: Int
    get() = mpptInputConfigurations.sumOf { it.numberOfPlannedModules }

  val numberOfStrings: Int
    get() = mpptInputConfigurations.sumOf { it.relevantStringConfigurations.size }


  @Serializable
  data class MpptInputConfiguration(val id: MpptInputConfigurationId = MpptInputConfigurationId.random(), val inputIndex: Int, val stringConfigurations: List<StringConfiguration>) : HasUuid {

    override val uuid: Uuid
      get() = id.uuid

    val relevantStringConfigurations: List<StringConfiguration>
      get() = stringConfigurations.filter { it.optimalModuleCount != null && it.optimalModuleCount > 0 }

    val numberOfOptimalModules: Int
      get() = relevantStringConfigurations.sumOf { it.optimalModuleCount ?: 0 }

    val numberOfPlannedModules: Int
      get() = relevantStringConfigurations.sumOf { it.totalModuleCount }

    val hasLegalOptimalModuleCount: Boolean
      get() = relevantStringConfigurations.all { stringConfiguration ->
        relevantStringConfigurations.all { otherStringConfiguration ->
          otherStringConfiguration.optimalModuleCount == stringConfiguration.optimalModuleCount
        }
      }

    fun forTheseLayouts(moduleLayouts: List<ModuleLayoutId>): MpptInputConfiguration {
      return copy(stringConfigurations = stringConfigurations.map { it.forTheseLayouts(moduleLayouts) })
    }

    fun contains(stringConfiguration: StringConfiguration): Boolean {
      return stringConfigurations.any { it.id == stringConfiguration.id }
    }

    fun format(): String {
      return "MPP-Eingang ${inputIndex + 1} (${'A' + inputIndex}): ${relevantStringConfigurations.size} Strings"
    }

    fun format(moduleLayouts: ResolvedModuleLayouts): String {
      return buildString {
        append(format())
        relevantStringConfigurations.forEach { stringConfiguration ->
          append("\n")
          append(stringConfiguration.format(moduleLayouts))
        }
      }
    }

    fun copy(stringConfigurations: List<StringConfiguration>): MpptInputConfiguration {
      return MpptInputConfiguration(id, inputIndex, stringConfigurations)
    }

    @Serializable
    data class MpptInputConfigurationId(val uuid: NativeUuid) {

      override fun toString(): String {
        return uuid.toString()
      }

      fun format(): String {
        return uuid.toString()
      }

      companion object {
        fun random(): MpptInputConfigurationId {
          return MpptInputConfigurationId(randomUuid4())
        }
      }
    }
  }

  @Serializable
  data class StringConfiguration(val id: StringConfigurationId = StringConfigurationId.random(), val stringIndex: Int, val optimalModuleCount: Int?, val modulesStrings: List<ModulesString>?) : HasUuid {

    override val uuid: Uuid
      get() = id.uuid

    val totalModuleCount: Int
      get() = modulesStrings?.sumOf { it.numberOfModules } ?: 0

    fun forTheseLayouts(moduleLayouts: List<ModuleLayoutId>): StringConfiguration {
      return copy(modulesStrings = modulesStrings?.filter { moduleLayouts.contains(it.moduleLayoutId) })
    }

    fun format(moduleLayouts: ResolvedModuleLayouts): String {
      return buildString {
        append("String ${stringIndex + 1}")
        append(" - ${optimalModuleCount ?: "Keine"} Module (Optimal)")
        modulesStrings?.fastForEach { roofString ->
          append("\n")
          append("${moduleLayouts[roofString.moduleLayoutId].layoutName}: ${roofString.numberOfModules} Module")
        }
      }
    }

    @Serializable
    data class StringConfigurationId(val uuid: NativeUuid) {

      override fun toString(): String {
        return uuid.toString()
      }

      fun format(): String {
        return uuid.toString()
      }

      companion object {
        fun random(): StringConfigurationId {
          return StringConfigurationId(randomUuid4())
        }
      }
    }
  }

  @Serializable
  data class InverterConfigurationId(override val uuid: NativeUuid): ValueClassUuid {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): InverterConfigurationId {
        return InverterConfigurationId(randomUuid4())
      }
    }
  }
}


fun RoofStringsConfiguration.toRestApiModel(inverterConfigurations: List<ResolvedInverterConfiguration>): List<ResolvedInverterConfiguration> {
  return inverterConfigurations.map { inverterConfiguration ->
    val newMpptInputConfigurations = inverterConfiguration.mpptInputConfigurations.map { mpptInputConfiguration ->
      val newStringConfigurations = mpptInputConfiguration.stringConfigurations.map { stringConfiguration ->
        val newModulesStrings = stringConfiguration.modulesStrings?.map { modulesString ->
          val meisterchartsRoofStrings = this.roofStrings.firstOrNull { it.uuid == modulesString.uuid } ?: return@map modulesString
          val stringOfModules = meisterchartsRoofStrings.modules.map { module ->
            ModuleReference(
              moduleAreaId = module.modulePlacement.moduleArea.id,
              moduleIndex = module.modulePlacement.moduleIndex,
            )
          }
          modulesString.copy(stringOfModules = stringOfModules)
        }
        stringConfiguration.copy(modulesStrings = newModulesStrings)
      }
      mpptInputConfiguration.copy(stringConfigurations = newStringConfigurations)
    }
    inverterConfiguration.copy(mpptInputConfigurations = newMpptInputConfigurations)
  }
}
